import { fromZonedTime } from 'date-fns-tz'
import { z } from 'zod'
import { ajouterJours, heureAParis, jourAParis } from './calendrier'
import {
  FUSEAU,
  REPORTS_DEMANDE,
  type ReportDemande,
  type StatutDemandeDisponibilite,
} from './domaine'
import { type NotificationDefi, texteSansDonnee } from './notifications'

// Demande de disponibilité (décision du 02/10/2026) : une connexion de portail qui a besoin d'un
// humain (code, validation, CAPTCHA) ne se relance jamais seule. Son titulaire reçoit d'abord une
// demande, une seule pour toutes ses connexions en attente, de 9 h à 20 h (heure de Paris) : il
// répond « C'est parti » (les collectes repartent, les vérifications arrivent dans les minutes qui
// suivent), « Dans 1 h » ou « Demain ». Sans réponse en trente minutes, la demande revient deux
// heures plus tard, jamais hors de la plage. On s'assure ainsi que quelqu'un est là avant qu'un
// fournisseur envoie un code.

/** Plage des demandes, heures pleines de Paris : de `debut` inclus à `fin` exclu. */
export const PLAGE_DEMANDES = { debut: 9, fin: 20 } as const
/** Délai de réponse d'une demande. */
export const MINUTES_REPONSE_DEMANDE = 30
/** Sans réponse : nouvelle demande après ce délai (dans la plage). */
export const MINUTES_RELANCE_DEMANDE = 120

const heurePleine = (h: number) => `${String(h).padStart(2, '0')}:00`
const ouvertureDu = (jour: string) =>
  fromZonedTime(`${jour}T${heurePleine(PLAGE_DEMANDES.debut)}:00`, FUSEAU)

export function dansPlageDemandes(instant: Date): boolean {
  const heure = Number(heureAParis(instant).slice(0, 2))
  return heure >= PLAGE_DEMANDES.debut && heure < PLAGE_DEMANDES.fin
}

/** L'instant lui-même s'il est dans la plage ; sinon 9 h du jour (avant 9 h) ou du lendemain. */
export function prochaineOuverturePlage(instant: Date): Date {
  if (dansPlageDemandes(instant)) return instant
  const jour = jourAParis(instant)
  const heure = Number(heureAParis(instant).slice(0, 2))
  return ouvertureDu(heure < PLAGE_DEMANDES.debut ? jour : ajouterJours(jour, 1))
}

/** Pas de nouvelle demande avant cet instant, après un report ou un silence. */
export function repriseDemande(motif: ReportDemande | 'sans_reponse', maintenant: Date): Date {
  switch (motif) {
    case 'une_heure':
      return prochaineOuverturePlage(new Date(maintenant.getTime() + 60 * 60_000))
    case 'demain':
      return ouvertureDu(ajouterJours(jourAParis(maintenant), 1))
    case 'sans_reponse':
      return prochaineOuverturePlage(
        new Date(maintenant.getTime() + MINUTES_RELANCE_DEMANDE * 60_000),
      )
  }
}

export type EvenementDemande = { type: 'accepter' | 'reporter' | 'expirer' | 'classer' }

export const TRANSITIONS_DEMANDE: readonly {
  evenement: EvenementDemande['type']
  depuis: readonly StatutDemandeDisponibilite[]
  vers: StatutDemandeDisponibilite
}[] = [
  { evenement: 'accepter', depuis: ['ouverte'], vers: 'acceptee' },
  { evenement: 'reporter', depuis: ['ouverte'], vers: 'reportee' },
  { evenement: 'expirer', depuis: ['ouverte'], vers: 'expiree' },
  { evenement: 'classer', depuis: ['ouverte'], vers: 'sans_objet' },
]

export function transitionDemande(
  etat: { statut: StatutDemandeDisponibilite },
  evenement: EvenementDemande,
): { statut: StatutDemandeDisponibilite } {
  const ligne = TRANSITIONS_DEMANDE.find(
    (l) => l.evenement === evenement.type && l.depuis.includes(etat.statut),
  )
  if (!ligne)
    throw new Error(`Demande : « ${evenement.type} » impossible depuis « ${etat.statut} ».`)
  return { statut: ligne.vers }
}

export const reportDemande = z.object({ quand: z.enum(REPORTS_DEMANDE) }).strict()

/** « A, B et C » ; au-delà de trois, « A, B, C et 2 autres ». */
export function libellesConnexions(libelles: readonly string[]): string {
  const montres = libelles.length > 3 ? libelles.slice(0, 3) : libelles
  const reste = libelles.length - montres.length
  const tete = reste > 0 ? montres : montres.slice(0, -1)
  const fin = reste > 0 ? `${reste} autre${reste > 1 ? 's' : ''}` : montres.at(-1)
  return tete.length === 0 ? (fin ?? '') : `${tete.join(', ')} et ${fin}`
}

const SUITE = 'un code ou une vérification va vous être demandé'

export function notificationDemande(d: {
  id: string
  libelles: readonly string[]
  expireLe: Date
}): NotificationDefi {
  const libelles = d.libelles.map((l) => texteSansDonnee(l, 60) || 'Portail')
  return {
    titre: 'Êtes-vous disponible ?',
    corps: `${libellesConnexions(libelles)} : ${SUITE}. Répondre avant ${heureAParis(d.expireLe)}.`,
    tag: `disponibilite-${d.id}`,
    // L'écran de la demande s'ouvre par-dessus n'importe quelle page de l'appli.
    url: '/',
    exigeInteraction: true,
    silencieuse: false,
  }
}

/** Message Slack de la demande, pour qui l'a choisi. */
export function texteDemande(
  libelles: readonly string[],
  expireLe: Date,
  lien: string | null,
): string {
  const plusieurs = libelles.length > 1
  const ou = lien ? ` dans l’appli : ${lien}` : ' dans l’appli.'
  // « Relève » plutôt que « pièces » : une connexion de justificatifs lit des opérations.
  return `${libellesConnexions(libelles)} ${plusieurs ? 'attendent' : 'attend'} votre présence pour ${plusieurs ? 'leur' : 'sa'} relève : ${SUITE}. Répondre avant ${heureAParis(expireLe)}${ou}`
}
